<template>
  <template v-if="isMinimized">
    <button
      @click="handleNavigation"
      class="bg-white rounded-lg py-2 px-4 list-none cursor-pointer transition-all duration-150 text-start flex flex-col gap-4"
      :class="[
        isSelected
          ? 'border-2 border-blue-500 shadow-md '
          : 'border-[1px] border-gray-200 hover:shadow-md'
      ]"
    >
      <!-- Cell 1: Logo and Title -->
      <div class="flex min-w-[min(1fr, 500px)] items-start flex-1">
        <img
          v-if="props.application.open_role_details?.employer_details?.logo"
          :src="props.application.open_role_details?.employer_details?.logo"
          class="w-12 h-12 mr-4 mt-1"
        />
        <span
          v-else
          class="w-12 h-12 mr-4 mt-1 bg-gray-100 flex justify-center items-center aspect-square"
        ></span>

        <div class="text-sm md:text-lg flex flex-col w-full">
          <h2>
            {{ props.application.open_role_details.role_name }}
          </h2>
          <span class="mt-1 font-light text-sm">{{
            props.application.open_role_details.employer_details.name
          }}</span>
        </div>
      </div>

      <!-- Cell 2: Status and Date -->
      <div class="flex flex-col gap-2 items-end min-w-[300px] ml-auto">
        <JobApplicationStatusBadge :status="props.application.application_status" status-type="application" />
        <div class="flex items-center text-gray-500">
          <Calendar class="w-4 h-4 mr-1" />
          <span class="text-gray-600 text-xs"
            >Updated {{ formatDate(props.application.updated_at, 'europe') }}</span
          >
        </div>
      </div>
    </button>
  </template>
  <template v-else>
    <button
      @click="handleNavigation"
      class="bg-white rounded-lg py-2 px-4 list-none cursor-pointer transition-all duration-150 text-start"
      :class="[
        isSelected
          ? 'border-2 border-blue-500 shadow-md '
          : 'border-[1px] border-gray-200 hover:shadow-md'
      ]"
    >
      <article class="flex flex-wrap gap-4">
        <!-- Top section -->
        <div class="flex flex-wrap w-full gap-4">
          <!-- Cell 1: Logo and Title -->
          <div class="flex min-w-[min(1fr, 500px)] items-start flex-1">
            <img
              v-if="props.application.open_role_details?.employer_details?.logo"
              :src="props.application.open_role_details?.employer_details?.logo"
              class="w-12 h-12 mr-4 mt-1"
            />
            <span
              v-else
              class="w-12 h-12 mr-4 mt-1 bg-gray-100 flex justify-center items-center aspect-square"
            >
              <Building class="w-6 h-6 text-gray-500" />
            </span>

            <div class="text-sm md:text-lg flex flex-col w-full">
              <h2 class="font-medium">
                {{ props.application.open_role_details.role_name }}
                <img
                  v-if="props.application.open_role_details.role_type !== 'PERSONAL'"
                  src="@/assets/outtalent_badge.svg"
                  class="inline-block ml-2 mb-[0.12rem] w-[1.1rem]"
                />
              </h2>
              <span class="font-light">{{
                props.application.open_role_details.employer_details.name
              }}</span>
            </div>
          </div>

          <!-- Cell 2: Status and Date -->
          <div class="flex flex-col gap-2 items-end min-w-[300px] ml-auto">
            <div class="flex gap-2">
              <JobApplicationStatusBadge
                v-if="!isLimitedUser"
                :status="props.application.referral_status"
                status-type="referralStatus"
              />
              <JobApplicationStatusBadge :status="props.application.application_status" status-type="application" />
              <JobApplicationStatusBadge :status="props.application.interview_status" status-type="interview" />
            </div>
            <div class="flex items-center text-gray-500">
              <Calendar class="w-4 h-4 mr-1" />
              <span class="text-gray-600 text-xs"
                >Updated {{ formatDate(props.application.updated_at, 'europe') }}</span
              >
            </div>
          </div>
        </div>

        <!-- Bottom section -->
        <div class="flex w-full gap-4 flex-wrap">
          <!-- Cell 3: Locations -->
          <div
            class="flex flex-wrap gap-2 text-xs md:text-sm items-center text-gray-600 flex-1 min-w-[400px]"
          >
            <LocationDisplay :data="props.application.open_role_details" text-size="sm" />
          </div>

          <!-- Cell 4: Job Status -->
          <div class="flex items-center gap-2 justify-end ml-auto">
            <span class="text-xs rounded-full px-2 py-1 w-fit border-[1px] border-gray-200">
              {{
                seniorityLevels.find(
                  (level) => level.id === props.application.open_role_details.seniority_level
                )?.tag
              }}
            </span>
            <span
              class="text-white text-xs capitalize rounded-full w-14 flex items-center justify-center py-1"
              :class="{
                'bg-lime-500': props.application.open_role_details.status_open,
                'bg-red-500': !props.application.open_role_details.status_open
              }"
              >{{ props.application.open_role_details.status_open ? 'Open' : 'Closed' }}</span
            >
          </div>
        </div>
      </article>
    </button>
  </template>
</template>

<script setup lang="ts">
import { defineProps, computed } from 'vue'
import { Calendar, Building } from 'lucide-vue-next'
import { useRoute, useRouter } from 'vue-router'
import { formatDate } from '@/utils/formatDate'
import seniorityLevels from '@/const/seniorityLevels'
import JobApplicationStatusBadge from '@/components/common/jobApplication/JobApplicationStatusBadge.vue'
import LocationDisplay from '@/components/common/LocationDisplay.vue'
import { useAuth } from '@/composables/useAuth'

const route = useRoute()
const router = useRouter()
const { isLimitedUser } = useAuth()

const isMinimized = computed(() => {
  return route.params.application_id
})

// Define the props
const props = defineProps({
  application: {
    type: Object,
    required: true
  },
  open_application_id: {
    type: [String, Number],
    default: null
  }
})

const handleNavigation = async () => {
  try {
    const navigationOptions = {
      name: 'User Application container',
      params: { application_id: String(props.application.id) },
      query: { ...route.query }
    }

    if (route.params.application_id) {
      await router.replace(navigationOptions)
    } else {
      await router.push(navigationOptions)
    }
  } catch (error) {
    console.error('Navigation error:', error)
    // Fallback navigation
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/applications')
  }
}

// Compute if this card is selected based on the URL parameter
const isSelected = computed(() => {
  return route.params.application_id === props.application.id.toString()
})
</script>
